import type { CurrentUser } from '@oasis/contracts';
import { LogIn } from 'lucide-react';
import type { User, UserManager } from 'oidc-client-ts';
import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';

import { Button, Loading } from './design/components.js';
import { saisieEnCours } from './miseajour/saisie.js';
import { api, JETON_REFUSE, type RuntimeConfig } from './runtime.js';
import {
  apresEchecRenouvellement,
  cibleRetour,
  delaiReessai,
  renouvellementDu,
  retourSur,
} from './session/renouvellement.js';
import './miseajour/miseajour.css';

/**
 * Session de l'utilisateur (connexion OIDC « code + PKCE », jetons en mémoire de session) :
 * fournie à toute l'interface. Le profil `/me` donne les rôles et l'état du second facteur.
 *
 * Révision du référent du 2026-10-31 : le renouvellement silencieux reprend après une veille
 * ou une coupure réseau (nouvel essai, retour sur l'onglet, retour du réseau) ; une session
 * réellement terminée ramène, après reconnexion, sur la page où l'on était, et une saisie en
 * cours est gardée (reconnexion dans une fenêtre, sans quitter la page).
 */
interface Session {
  user: User;
  me: CurrentUser;
  config: RuntimeConfig;
  signOut: () => Promise<void>;
}

const SessionContext = createContext<Session | null>(null);

export function useSession(): Session {
  const session = useContext(SessionContext);
  if (session === null) throw new Error('Session absente');
  return session;
}

const pageCourante = (): string => cibleRetour(window.location);

const CLE_ECHEC_RETOUR = 'oasis.session.echec-retour';

/** Premier retour de Keycloak inexploitable depuis 30 s ? (évite toute boucle) */
function premierEchecDeRetour(): boolean {
  try {
    const avant = Number(window.sessionStorage.getItem(CLE_ECHEC_RETOUR));
    window.sessionStorage.setItem(CLE_ECHEC_RETOUR, String(Date.now()));
    return !(Date.now() - avant < 30_000);
  } catch {
    return false;
  }
}

export function SessionProvider({
  config,
  users,
  children,
  fallback,
}: {
  config: RuntimeConfig;
  users: UserManager;
  children: ReactNode;
  /** Rendu quand l'API est indisponible (maintenance, erreur). */
  fallback: (error: unknown) => ReactElement;
}): ReactElement {
  const [session, setSession] = useState<Session | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [expiree, setExpiree] = useState(false);

  useEffect(() => {
    let actif = true;
    let enCours: Promise<void> | null = null;
    let tentative = 0;
    let reessai: number | undefined;

    const reconnecter = (): Promise<void> => users.signinRedirect({ state: pageCourante() });

    /** Session terminée : retour immédiat par Keycloak, sauf saisie à protéger. */
    const terminee = (): void => {
      if (!actif) return;
      if (saisieEnCours()) setExpiree(true);
      else void reconnecter();
    };

    const renouveler = (): Promise<void> => {
      if (enCours !== null) return enCours;
      window.clearTimeout(reessai);
      enCours = users
        .signinSilent()
        .then(() => {
          tentative = 0;
        })
        .catch((e: unknown) => {
          if (apresEchecRenouvellement(e) === 'expiree') {
            terminee();
            return;
          }
          // Réseau absent (sortie de veille) : nouvel essai, et dès le retour du réseau.
          reessai = window.setTimeout(() => void renouveler(), delaiReessai(tentative));
          tentative += 1;
        })
        .finally(() => {
          enCours = null;
        });
      return enCours;
    };

    const init = async (): Promise<void> => {
      if (new URLSearchParams(window.location.search).has('code')) {
        let back: User | undefined;
        try {
          // Retour de Keycloak : redirection, ou fenêtre de reconnexion (refermée seule).
          back = await users.signinCallback();
          if (back === undefined) return;
        } catch (e) {
          // Retour inexploitable (page de retour rechargée, fenêtre de reconnexion qui a perdu
          // sa page d'origine) : on repart de la page sans le code, une seule fois ; la session
          // Keycloak évite de redemander le mot de passe.
          if (!premierEchecDeRetour()) throw e;
        }
        window.history.replaceState(
          {},
          '',
          back === undefined ? pageCourante() : retourSur(back.state),
        );
      }
      let current = await users.getUser();
      if (current !== null && current.expired === true && current.refresh_token !== undefined) {
        // Jeton expiré (onglet resté ouvert, veille) : la session Keycloak suffit souvent.
        current = await users.signinSilent().catch(() => null);
      }
      if (current === null || current.expired === true) {
        await reconnecter();
        return;
      }
      const me = await api<CurrentUser>(current, '/me');
      if (!actif) return;
      setSession({ user: current, me, config, signOut: () => users.signoutRedirect() });
    };
    init().catch(setError);

    // Jeton renouvelé en silence : la session suit le nouveau jeton.
    const renewed = (user: User): void => {
      setSession((s) => (s === null ? s : { ...s, user }));
    };
    const echec = (): void => {
      void renouveler();
    };
    const verifier = (): void => {
      if (document.visibilityState !== 'visible' || !navigator.onLine) return;
      void users.getUser().then((u) => {
        if (u !== null && renouvellementDu(u.expires_in)) void renouveler();
      });
    };
    users.events.addUserLoaded(renewed);
    users.events.addSilentRenewError(echec);
    users.events.addAccessTokenExpired(echec);
    window.addEventListener(JETON_REFUSE, echec);
    window.addEventListener('online', verifier);
    document.addEventListener('visibilitychange', verifier);
    return () => {
      actif = false;
      window.clearTimeout(reessai);
      users.events.removeUserLoaded(renewed);
      users.events.removeSilentRenewError(echec);
      users.events.removeAccessTokenExpired(echec);
      window.removeEventListener(JETON_REFUSE, echec);
      window.removeEventListener('online', verifier);
      document.removeEventListener('visibilitychange', verifier);
    };
  }, [users, config]);

  if (error !== null) return fallback(error);
  if (session === null) return <Loading label="Connexion…" />;
  return (
    <SessionContext.Provider value={session}>
      {children}
      {expiree && (
        <SessionExpiree
          users={users}
          sujet={session.user.profile.sub}
          onReconnecte={() => {
            setExpiree(false);
          }}
        />
      )}
    </SessionContext.Provider>
  );
}

/**
 * Session terminée pendant une saisie : reconnexion dans une fenêtre, la page et sa saisie
 * restent en place. Fenêtre bloquée ou fermée : reconnexion dans la page (saisie perdue).
 */
function SessionExpiree({
  users,
  sujet,
  onReconnecte,
}: {
  users: UserManager;
  sujet: string;
  onReconnecte: () => void;
}): ReactElement {
  const [fenetreEchouee, setFenetreEchouee] = useState(false);
  const fenetre = (): void => {
    users
      .signinPopup()
      .then((u) => {
        if (u.profile.sub !== sujet) window.location.assign('/');
        else onReconnecte();
      })
      .catch(() => {
        setFenetreEchouee(true);
      });
  };
  return (
    <div className="o-maj" role="alert">
      <LogIn aria-hidden="true" size={18} className="o-maj__icone" />
      <p className="o-maj__texte">
        <strong>Votre session a expiré.</strong>{' '}
        {fenetreEchouee
          ? 'La fenêtre de connexion n’a pas pu s’ouvrir. Copiez votre saisie si besoin, puis reconnectez-vous ici.'
          : 'Votre saisie est gardée : reconnectez-vous, puis enregistrez.'}
      </p>
      <div className="o-maj__actions">
        {fenetreEchouee ? (
          <Button
            variant="primary"
            small
            onClick={() => void users.signinRedirect({ state: pageCourante() })}
          >
            Se reconnecter ici
          </Button>
        ) : (
          <Button variant="primary" small onClick={fenetre}>
            Se reconnecter
          </Button>
        )}
      </div>
    </div>
  );
}
